import { ref, watch, computed } from '@vue/composition-api';
import store from '@/store';
import { formatDateBr } from '@core/utils/filter';
import HbiImagemHomeService from '@/services/SistemaDKP/sls-imagem-home.service';
// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import SisDkpClientesService from '@/services/sis-dkp-clientes.service';

export default function useUsersList(nameModule) {
  // Use toast
  const sisDkpClientesService = new SisDkpClientesService();

  const toast = useToast();
  const imagemHomeService = new HbiImagemHomeService();
  const refDataTableTemplateImagem = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: 'id', label: '#', sortable: true },
    { key: 'nome_fantasia', label: 'Nome Fantasia', sortable: true },
    { key: 'razao_social', label: 'Razão Social', sortable: true },
    { key: 'cnpj', label: 'CNPJ', sortable: true },
    { key: 'subdominio', label: 'Subdomínio', sortable: true },
    {
      key: 'updated_at', label: 'Data Alteração', sortable: true, formatter: (value) => formatDateBr(value),
    },
    { key: 'actions', label: 'Ações', sortable: false },
  ];
  const perPage = ref(10);
  const totalTemplateImagem = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');
  const sortBy = ref('id');
  const isSortDirDesc = ref(true);
  const overlay = ref(true);
  const roleFilter = ref(null);
  const planFilter = ref(null);
  const statusFilter = ref(null);

  const dataMeta = computed(() => {
    const localItemsCount = refDataTableTemplateImagem.value ? refDataTableTemplateImagem.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalTemplateImagem.value,
    };
  });

  const refetchData = () => {
    refDataTableTemplateImagem.value.refresh();
  };

  watch([currentPage, perPage, searchQuery, roleFilter, planFilter, statusFilter], () => {
    refetchData();
  });

  const pagination = computed({
    get() {
      return store.getters['sisDkpClientes/GET_FILTERS'];
    },
    set(value) {
      store.commit('sisDkpClientes/SET_FILTERS', value);
    },
  });

  const createOrUpdated = async (body) => {
    if (body.id) {
      await sisDkpClientesService.edit(body.id, body);
      refetchData();
      return;
    }
    await sisDkpClientesService.create(body);
    refetchData();
  };

  const deleteCliente = async (id) => {
    await sisDkpClientesService.delete(id);
    refetchData();
  };

  const editCliente = async (id, body) => {
    await sisDkpClientesService.edit(id, body);
    refetchData();
  };
  const fetchDataAll = (ctx, callback) => {
    overlay.value = true;
    store
      .dispatch('sisDkpClientes/findAll', {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
      })
      .then((response) => {
        const { data } = response;
        overlay.value = false;
        callback(data);
        perPage.value = pagination.value.perPage;
        currentPage.value = pagination.value.page;
        totalTemplateImagem.value = pagination.value.total;
      })
      .catch((error) => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error ao carregar dados',
            text: error.message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      });
  };

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = (role) => {
    if (role === 'subscriber') return 'primary';
    if (role === 'author') return 'warning';
    if (role === 'maintainer') return 'success';
    if (role === 'editor') return 'info';
    if (role === 'admin') return 'danger';
    return 'primary';
  };

  const resolveUserRoleIcon = (role) => {
    if (role === 'subscriber') return 'UserIcon';
    if (role === 'author') return 'SettingsIcon';
    if (role === 'maintainer') return 'DatabaseIcon';
    if (role === 'editor') return 'Edit2Icon';
    if (role === 'admin') return 'ServerIcon';
    return 'UserIcon';
  };

  const resolveUserStatusVariant = (status) => {
    if (status === 'pending') return 'warning';
    if (status === 'active') return 'success';
    if (status === 'inactive') return 'secondary';
    return 'primary';
  };

  return {
    fetchDataAll,
    tableColumns,
    perPage,
    currentPage,
    totalTemplateImagem,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refDataTableTemplateImagem,
    overlay,
    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    refetchData,

    // Extra Filters
    roleFilter,
    planFilter,
    statusFilter,

    createOrUpdated,
    deleteCliente,
    editCliente,
  };
}
